import React from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const InvestigationStatistics = ({ investigations, recommendations }) => {
  const statusDistribution = [
    { name: 'Initiated', value: investigations?.filter(i => i?.status === 'initiated')?.length, color: '#3b82f6' },
    { name: 'Ongoing', value: investigations?.filter(i => i?.status === 'ongoing')?.length, color: '#f59e0b' },
    { name: 'Completed', value: investigations?.filter(i => i?.status === 'completed')?.length, color: '#10b981' },
    { name: 'Overdue', value: investigations?.filter(i => i?.status === 'overdue')?.length, color: '#ef4444' }
  ];

  const incidentTypeDistribution = [
    { 
      name: 'Near Miss', 
      value: investigations?.filter(i => i?.incidentType === 'near-miss')?.length,
      color: '#fbbf24'
    },
    { 
      name: 'Injury', 
      value: investigations?.filter(i => i?.incidentType === 'injury')?.length,
      color: '#f97316'
    },
    { 
      name: 'Fatality', 
      value: investigations?.filter(i => i?.incidentType === 'fatality')?.length,
      color: '#dc2626'
    }
  ];

  const completionTimeData = [
    { month: 'Aug', avgDays: 12 },
    { month: 'Sep', avgDays: 10 },
    { month: 'Oct', avgDays: 9 },
    { month: 'Nov', avgDays: 8.5 },
    { month: 'Dec', avgDays: 8 },
    { month: 'Jan', avgDays: 8.5 }
  ];

  const recommendationStatusData = [
    { 
      name: 'Pending', 
      value: recommendations?.filter(r => r?.status === 'pending')?.length,
      color: '#9ca3af'
    },
    { 
      name: 'In Progress', 
      value: recommendations?.filter(r => r?.status === 'in-progress')?.length,
      color: '#3b82f6'
    },
    { 
      name: 'Completed', 
      value: recommendations?.filter(r => r?.status === 'completed')?.length,
      color: '#10b981'
    },
    { 
      name: 'Overdue', 
      value: recommendations?.filter(r => r?.status === 'overdue')?.length,
      color: '#ef4444'
    }
  ];

  const locationPerformanceData = [
    { 
      location: 'Plant 1',
      investigations: investigations?.filter(i => i?.location === 'plant-1')?.length,
      avgCompletionTime: 7.5
    },
    { 
      location: 'Plant 2',
      investigations: investigations?.filter(i => i?.location === 'plant-2')?.length,
      avgCompletionTime: 9
    },
    { 
      location: 'Plant 3',
      investigations: investigations?.filter(i => i?.location === 'plant-3')?.length,
      avgCompletionTime: 8
    },
    { 
      location: 'Warehouse 1',
      investigations: investigations?.filter(i => i?.location === 'warehouse-1')?.length,
      avgCompletionTime: 10
    }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="PieChart" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Investigation Status Distribution</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {statusDistribution?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="AlertTriangle" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Incident Type Distribution</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={incidentTypeDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {incidentTypeDistribution?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="TrendingDown" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Average Investigation Completion Time Trend</h3>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={completionTimeData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="avgDays" stroke="#3b82f6" strokeWidth={2} name="Avg Completion Days" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="CheckCircle" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Recommendation Implementation Status</h3>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={recommendationStatusData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" name="Recommendations">
              {recommendationStatusData?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry?.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="MapPin" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Location Performance Analysis</h3>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={locationPerformanceData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="location" />
            <YAxis yAxisId="left" orientation="left" label={{ value: 'Investigations', angle: -90, position: 'insideLeft' }} />
            <YAxis yAxisId="right" orientation="right" label={{ value: 'Avg Days', angle: 90, position: 'insideRight' }} />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="investigations" fill="#3b82f6" name="Total Investigations" />
            <Bar yAxisId="right" dataKey="avgCompletionTime" fill="#10b981" name="Avg Completion Time (Days)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Target" size={20} className="text-emerald-600" />
            <span className="text-2xl font-bold text-foreground">
              {Math.round((investigations?.filter(i => i?.status === 'completed')?.length / investigations?.length) * 100)}%
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Investigation Closure Rate</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={20} className="text-amber-600" />
            <span className="text-2xl font-bold text-foreground">8.5</span>
          </div>
          <p className="text-sm text-muted-foreground">Avg Days to Complete</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckSquare" size={20} className="text-blue-600" />
            <span className="text-2xl font-bold text-foreground">
              {Math.round((recommendations?.filter(r => r?.status === 'completed')?.length / recommendations?.length) * 100)}%
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Recommendation Implementation Rate</p>
        </div>
      </div>
    </div>
  );
};

export default InvestigationStatistics;